import { useEffect } from 'react';

/**
 * Requires a control to declare an accessible name exactly one of three ways:
 * an `aria-label`, an `aria-labelledby`, or an `id` referenced by an external
 * `<label htmlFor>` (e.g. the automatic association when composed inside a
 * `Field`). Intersect this with a control's other props so the type enforces a
 * name at every call site.
 */
export type AccessibleNameProps =
  | { 'aria-label': string; 'aria-labelledby'?: undefined; id?: string }
  | { 'aria-labelledby': string; 'aria-label'?: undefined; id?: string }
  | { id: string; 'aria-label'?: undefined; 'aria-labelledby'?: undefined };

/**
 * {@link AccessibleNameProps} extended with a component-specific name source
 * (e.g. Checkbox's `caption`): either the extra source is present (and the
 * standard ones stay optional, keeping their mutual-exclusivity shape), or a
 * standard source is present (and the extra one stays optional). The
 * exclusivity rule lives here, in exactly one place.
 */
export type AccessibleNameWith<Extra> =
  | (Extra & Partial<AccessibleNameProps>)
  | (AccessibleNameProps & Partial<Extra>);

/**
 * Runtime mirror of the {@link AccessibleNameProps} constraint: any of
 * `aria-label`, `aria-labelledby`, or an `id` (assumed to be referenced by an
 * external `<label htmlFor>`) names the control. Components with an additional
 * name source (e.g. Checkbox's `caption`) pass it as `extraNameSource`. An
 * empty string is not a name.
 */
export function hasAccessibleName(
  props: { id?: string; 'aria-label'?: string; 'aria-labelledby'?: string },
  extraNameSource = false,
): boolean {
  return Boolean(props['aria-label'] || props['aria-labelledby'] || props.id) || extraNameSource;
}

/**
 * Dev-only backstop for the {@link AccessibleNameProps} type constraint: logs a
 * `console.warn` (development builds only) when a control that requires an
 * accessible name was rendered without one. The type system enforces this for
 * TypeScript callers; this catches JS consumers and `as any` escapes that slip
 * past it. Compute the flag with {@link hasAccessibleName} so every component
 * shares one definition of "named".
 *
 * Always called so hook order stays stable; the effect no-ops in production.
 */
export function useAccessibleNameWarning(componentName: string, hasName: boolean): void {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (hasName) return;
    console.warn(
      `[Atoms] <${componentName}> was rendered without an accessible name. Provide ` +
        '`aria-label`, `aria-labelledby`, or an `id` referenced by a `<label htmlFor>` ' +
        '(for example via `Field`/`Form`). Without one, assistive technology cannot ' +
        'announce the control.',
    );
  }, [componentName, hasName]);
}

/**
 * Dev-only counterpart to {@link useAccessibleNameWarning}: warns (development
 * builds only) when a control is given *more than one* accessible name — e.g.
 * Checkbox's visible `caption` alongside an `aria-label`/`aria-labelledby`. The
 * `aria-*` value wins and silently suppresses the visible label from the
 * accessible name, so assistive technology announces something other than what's
 * shown. Pass `true` for `hasConflict` when competing sources are present.
 *
 * Always called so hook order stays stable; the effect no-ops in production.
 */
export function useAccessibleNameConflictWarning(
  componentName: string,
  hasConflict: boolean,
): void {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!hasConflict) return;
    console.warn(
      `[Atoms] <${componentName}> was given more than one accessible name (for example ` +
        'a visible `caption` together with `aria-label`/`aria-labelledby`). The `aria-*` ' +
        'value overrides the visible label for assistive technology — provide exactly one.',
    );
  }, [componentName, hasConflict]);
}
